import React from "react";
import { motion } from "framer-motion";
import { MapPin, Clock, ExternalLink } from "lucide-react";
import SectionReveal from "./SectionReveal";
import Divider from "./Divider";

const VENUE_ADDR = "36 Montvale Ave, Woburn, MA 01801";
const MAP_URL = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
  "Victoria Hall " + VENUE_ADDR
)}`;

const EVENTS = [
  {
    id: "nikah",
    arabic: "النِّكَاح",
    arabicTransliteration: "Nikāḥ",
    name: "Nikah",
    time: "6:00 PM onwards",
    tagline: "The sacred vows",
    description:
      "Join us as we recite the words that bind two souls in the eyes of Allāh.",
  },
  {
    id: "reception",
    arabic: "الْوَلِيمَة",
    arabicTransliteration: "Walīmah",
    name: "Reception",
    time: "Right after Nikah",
    tagline: "Celebration & feast",
    description:
      "A blessed walīmah — dinner, du'ās, and joy shared with our loved ones.",
  },
];

function EventCard({ event, index }) {
  const isRight = index % 2 === 1;
  return (
    <SectionReveal delay={index * 0.18} y={50}>
      <div
        data-testid={`event-card-${event.id}`}
        className="relative mx-auto max-w-md"
      >
        {/* Animated number marker */}
        <div className="flex items-center justify-center mb-6">
          <span className="font-serif italic text-[10px] tracking-[0.5em] uppercase text-champagne/80">
            ❖ Event {String(index + 1).padStart(2, "0")} of 02 ❖
          </span>
        </div>

        <div className="relative">
          {/* Decorative organic blob behind card */}
          <motion.div
            aria-hidden="true"
            initial={{ opacity: 0, scale: 0.85 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, ease: "easeOut" }}
            className={`absolute -inset-3 ${
              isRight ? "ink-blob-3" : "ink-blob-2"
            } bg-gradient-to-br from-parchment to-champagne/15 -z-10`}
          />

          {/* Main card */}
          <div className="relative glass-card rounded-[28px] px-7 py-10 text-center">
            {/* Arabic at top with shimmer line */}
            <div className="flex items-center justify-center gap-3 mb-2">
              <span className="h-px w-8 bg-champagne/50" />
              <p className="font-arabic text-3xl text-champagne leading-none">
                {event.arabic}
              </p>
              <span className="h-px w-8 bg-champagne/50" />
            </div>

            <p className="text-[10px] uppercase tracking-[0.5em] text-ink/50 mb-5">
              {event.arabicTransliteration}
            </p>

            {/* Event name in display script */}
            <motion.h3
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.2 }}
              className="font-serif italic font-light text-ink text-5xl sm:text-6xl mb-2"
            >
              {event.name}
            </motion.h3>

            <p className="text-xs italic tracking-wide text-ink/55">
              {event.tagline}
            </p>

            {/* Ornament */}
            <div className="my-6 flex items-center justify-center gap-2 text-champagne">
              <span className="h-px w-12 bg-champagne/40" />
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <motion.circle
                  cx="12"
                  cy="12"
                  r="3"
                  fill="currentColor"
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                />
              </svg>
              <span className="h-px w-12 bg-champagne/40" />
            </div>

            {/* Description */}
            <p className="text-sm text-ink/65 leading-relaxed font-serif italic max-w-xs mx-auto">
              {event.description}
            </p>

            {/* Time */}
            <div className="mt-7 flex items-center justify-center gap-2 text-ink">
              <Clock size={14} className="text-champagne" strokeWidth={1.6} />
              <p className="text-[11px] uppercase tracking-[0.35em]">
                {event.time}
              </p>
            </div>
          </div>
        </div>
      </div>
    </SectionReveal>
  );
}

export default function Events() {
  return (
    <section
      id="events"
      data-testid="events-section"
      className="relative py-20 px-6"
    >
      <div className="max-w-md mx-auto text-center mb-14">
        <SectionReveal>
          <p className="uppercase text-[11px] tracking-[0.45em] text-ink/55 mb-3">
            The celebrations
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl italic text-ink">
            Two moments,
            <br />
            <span className="shimmer-gold">one evening.</span>
          </h2>
          <p className="font-arabic text-xl text-champagne mt-4">
            بَارَكَ ٱللَّٰهُ لَكُمَا
          </p>
          <p className="text-xs italic text-ink/55 mt-1">
            &ldquo;May Allāh bless you both&rdquo;
          </p>
        </SectionReveal>
      </div>

      <div className="space-y-20">
        {EVENTS.map((e, i) => (
          <EventCard key={e.id} event={e} index={i} />
        ))}
      </div>

      {/* Shared Venue Block */}
      <SectionReveal delay={0.2}>
        <div
          data-testid="venue-block"
          className="mt-20 max-w-md mx-auto text-center"
        >
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-12 bg-champagne/50" />
            <p className="text-[10px] uppercase tracking-[0.5em] text-champagne">
              The venue
            </p>
            <span className="h-px w-12 bg-champagne/50" />
          </div>

          <div className="flex flex-col items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-champagne/15 flex items-center justify-center">
              <MapPin size={18} className="text-champagne" strokeWidth={1.4} />
            </div>
            <h4 className="font-serif italic text-3xl text-ink">
              Victoria Hall
            </h4>
            <p className="text-sm text-ink/65 leading-relaxed">{VENUE_ADDR}</p>
            <a
              href={MAP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="venue-map-link"
              className="mt-2 inline-flex items-center gap-1.5 text-xs tracking-[0.25em] uppercase text-ink border border-champagne/40 rounded-full px-5 py-2.5 hover:bg-ink hover:text-cotton hover:border-ink transition-all duration-300"
            >
              Open in Maps
              <ExternalLink size={12} strokeWidth={1.8} />
            </a>
          </div>
        </div>
      </SectionReveal>

      <Divider />
    </section>
  );
}
